import { PERM, type PermissionAction } from '@/shared/auth/types'
import { getConfig } from '@/shared/config'
import { BILLING_NAV } from '@/features/billing/nav'
import type { IconName } from './icons'
import { useBillingBadges } from '@/features/billing/useBillingBadges'
import { PIUTANG_NAV } from '@/features/piutang/nav'
import { usePiutangBadges } from '@/features/piutang/usePiutangBadges'
import type { NavBadges, NavSection } from './nav'

/**
 * Every module of SIPAY, in the order people see them.
 *
 * This list is the single place a module is declared. The module switcher in
 * the top bar, the module cards on Beranda and the command palette all read
 * it, so adding a module here is what makes it appear everywhere at once --
 * and forgetting one of those three is no longer possible.
 *
 * The recipe for a new module is in docs/konvensi-ui.md.
 */

export type ModuleTone = 'primary' | 'info' | 'success' | 'warning'

interface ModuleBase {
  id: string
  /** Full name, on Beranda cards: "SIPAY Billing". */
  name: string
  /** Short name, in the top bar and the sidebar header: "Billing". */
  short: string
  /** One line under the name in the switcher and the sidebar header. */
  tagline: string
  /** One sentence on the Beranda card. */
  description: string
  icon: IconName
  tone: ModuleTone
  /** The PORTAL_* permission that makes this module visible at all. */
  permCode: string
  action?: PermissionAction
}

/** A module that runs inside this application, under ModuleShell. */
export interface InternalModule extends ModuleBase {
  kind: 'internal'
  /** Route prefix; also where the switcher and the Beranda card lead. */
  basePath: string
  nav: NavSection[]
  /**
   * Counts for the sidebar, as a hook so it can use queries. Optional: a
   * module with nothing waiting on anyone simply has no badges.
   */
  useBadges?: () => NavBadges
}

/**
 * Another application. It opens in a new tab and says so, because until
 * single sign-on exists it also asks for a second login.
 */
export interface ExternalModule extends ModuleBase {
  kind: 'external'
  /** Read at call time: the URL comes from runtime config, not the build. */
  href: () => string | undefined
  /** The honest caveat shown under the link. */
  hint: string
}

export type ModuleDef = InternalModule | ExternalModule

export const BILLING_MODULE: InternalModule = {
  kind: 'internal',
  id: 'billing',
  name: 'SIPAY Billing',
  short: 'Billing',
  tagline: 'Proforma sampai invoice',
  description: 'Proforma, persetujuan berjenjang, dan invoice.',
  icon: 'receipt',
  tone: 'primary',
  permCode: PERM.PORTAL_BILLING,
  basePath: '/billing',
  nav: BILLING_NAV,
  useBadges: useBillingBadges,
}

export const PIUTANG_MODULE: InternalModule = {
  kind: 'internal',
  id: 'piutang',
  name: 'SIPAY Piutang',
  short: 'Piutang',
  tagline: 'Monitoring invoice dan penagihan',
  description: 'Pengantaran invoice, umur piutang, dan surat penagihan ke klien.',
  icon: 'wallet',
  tone: 'success',
  permCode: PERM.PORTAL_PIUTANG,
  basePath: '/piutang',
  nav: PIUTANG_NAV,
  useBadges: usePiutangBadges,
}

export const MONITORING_MODULE: ExternalModule = {
  kind: 'external',
  id: 'monitoring',
  name: 'SIPAY Monitoring',
  short: 'Monitoring',
  tagline: 'Transaksi, host, dan pelanggan',
  description: 'Pemantauan transaksi, host, dan pelanggan.',
  icon: 'pulse',
  tone: 'info',
  permCode: PERM.PORTAL_MONITORING,
  href: () => getConfig().mintUrl || undefined,
  // Single sign-on between the two apps is phase 2, after the ERP cutover: it
  // needs changes to the monitoring app, which is live and frozen until then.
  hint: 'Tab baru, perlu masuk sekali lagi',
}

export const MODULES: ModuleDef[] = [BILLING_MODULE, PIUTANG_MODULE, MONITORING_MODULE]

type CanFn = (permCode: string, action?: PermissionAction) => boolean

/**
 * The modules this person may open. Hidden rather than disabled, like menu
 * items; an external module without a configured URL is hidden too, since a
 * link to nowhere is worse than no link.
 */
export function visibleModules(can: CanFn): ModuleDef[] {
  return MODULES.filter((module) => {
    if (!can(module.permCode, module.action ?? 'view')) return false
    return module.kind === 'internal' || Boolean(module.href())
  })
}

/**
 * Tailwind needs whole class names in the source to generate them, so the
 * tones are spelled out rather than assembled from `bg-${tone}`.
 */
export const TONE_CLASS: Record<ModuleTone, { soft: string; solid: string; text: string }> = {
  primary: { soft: 'bg-primary-light text-primary', solid: 'bg-primary text-white', text: 'text-primary' },
  info: { soft: 'bg-info-light text-info', solid: 'bg-info text-white', text: 'text-info' },
  success: { soft: 'bg-success-light text-success', solid: 'bg-success text-white', text: 'text-success' },
  warning: { soft: 'bg-warning-light text-gray-800', solid: 'bg-warning text-black/85', text: 'text-gray-800' },
}

/** The same tones as raw colours, for gradients that no utility class can express. */
export const TONE_VAR: Record<ModuleTone, string> = {
  primary: 'var(--sp-primary)',
  info: 'var(--sp-info)',
  success: 'var(--sp-success)',
  warning: 'var(--sp-warning)',
}
